import { z } from 'zod';

import { CustomerDeliveryChannelSchema } from './customer-delivery-channel.schema.js';

export const SUGGESTED_PAYMENT_METHODS = [
  'PIX',
  'CREDIT_CARD',
  'DEBIT_CARD',
  'CASH',
  'BOLETO',
  'CUSTOMER_CREDIT',
] as const;

export const SuggestedPaymentMethodSchema = z.enum(SUGGESTED_PAYMENT_METHODS);

export type SuggestedPaymentMethod = z.infer<typeof SuggestedPaymentMethodSchema>;

export const PART_LINE_PRICE_ORIGINS = [
  'POLICY',
  'OVERRIDE',
  'CAMPAIGN',
  'MISSING',
  'MANUAL',
  'RFQ',
] as const;

export const PartLinePriceOriginSchema = z.enum(PART_LINE_PRICE_ORIGINS);

export type PartLinePriceOrigin = z.infer<typeof PartLinePriceOriginSchema>;

export const UpdateQuoteSuggestedPaymentBodySchema = z.object({
  suggestedPaymentMethod: SuggestedPaymentMethodSchema.nullable(),
});

export type UpdateQuoteSuggestedPaymentBody = z.infer<typeof UpdateQuoteSuggestedPaymentBodySchema>;

export const UpdateQuoteSuggestedPaymentResponseSchema = z.object({
  quoteId: z.uuid(),
  suggestedPaymentMethod: SuggestedPaymentMethodSchema.nullable(),
});

export type UpdateQuoteSuggestedPaymentResponse = z.infer<
  typeof UpdateQuoteSuggestedPaymentResponseSchema
>;

export const SetServiceQuoteSellerBodySchema = z.object({
  sellerUserId: z.uuid().nullable(),
});

export type SetServiceQuoteSellerBody = z.infer<typeof SetServiceQuoteSellerBodySchema>;

export const SetServiceQuoteSellerResponseSchema = z.object({
  quoteId: z.uuid(),
  sellerUserId: z.uuid().nullable(),
});

export type SetServiceQuoteSellerResponse = z.infer<typeof SetServiceQuoteSellerResponseSchema>;

export const UpdateQuoteDiagnosticsBodySchema = z.object({
  diagnosticText: z.string().max(5000).nullable(),
  technicalObservations: z.string().max(5000).nullable(),
});

export type UpdateQuoteDiagnosticsBody = z.infer<typeof UpdateQuoteDiagnosticsBodySchema>;

export const UpdateQuoteDiagnosticsResponseSchema = z.object({
  quoteId: z.uuid(),
  diagnosticText: z.string().nullable(),
  technicalObservations: z.string().nullable(),
});

export type UpdateQuoteDiagnosticsResponse = z.infer<typeof UpdateQuoteDiagnosticsResponseSchema>;

export const QuoteAttachmentOriginSchema = z.enum(['CHECK_IN', 'QUOTE']);

export type QuoteAttachmentOrigin = z.infer<typeof QuoteAttachmentOriginSchema>;

export const QuoteAttachmentSchema = z.object({
  id: z.uuid(),
  origin: QuoteAttachmentOriginSchema,
  objectUrl: z.string(),
  caption: z.string().nullable(),
  alt: z.string(),
});

export type QuoteAttachment = z.infer<typeof QuoteAttachmentSchema>;

export const AddQuoteAttachmentBodySchema = z.object({
  storageUrl: z.string().trim().min(1).max(500),
  caption: z.string().max(500).nullable().optional(),
});

export type AddQuoteAttachmentBody = z.infer<typeof AddQuoteAttachmentBodySchema>;

export const AddQuoteAttachmentResponseSchema = z.object({
  attachmentId: z.uuid(),
});

export type AddQuoteAttachmentResponse = z.infer<typeof AddQuoteAttachmentResponseSchema>;

export const RemoveQuoteAttachmentResponseSchema = z.object({
  attachmentId: z.uuid(),
});

export type RemoveQuoteAttachmentResponse = z.infer<typeof RemoveQuoteAttachmentResponseSchema>;

export const CUSTOMER_DECISION_CHANNELS = ['WHATSAPP', 'PHONE', 'IN_PERSON', 'EMAIL'] as const;

export const CustomerDecisionChannelSchema = z.enum(CUSTOMER_DECISION_CHANNELS);

export type CustomerDecisionChannel = z.infer<typeof CustomerDecisionChannelSchema>;

export const AddQuoteItemBodySchema = z
  .object({
    itemType: z.enum(['PRODUCT', 'SERVICE', 'GENERIC_PRODUCT', 'GENERIC_SERVICE']),
    placeProductId: z.uuid().optional(),
    tenantServiceId: z.uuid().optional(),
    catalogGenericProductId: z.uuid().optional(),
    catalogGenericServiceId: z.uuid().optional(),
    quantity: z.union([z.string(), z.number()]),
    unitPriceCents: z.union([z.string(), z.number()]),
    currency: z.string().length(3).optional(),
    warrantyFlag: z.boolean().optional(),
    warrantyUntil: z.iso.date().nullable().optional(),
    dependsOnQuoteItemId: z.uuid().nullable().optional(),
  })
  .superRefine((body, ctx) => {
    if (body.itemType === 'PRODUCT' && body.placeProductId === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'placeProductId required',
        path: ['placeProductId'],
      });
    }
    if (body.itemType === 'SERVICE' && body.tenantServiceId === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'tenantServiceId required',
        path: ['tenantServiceId'],
      });
    }
    if (body.itemType === 'GENERIC_PRODUCT' && body.catalogGenericProductId === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'catalogGenericProductId required',
        path: ['catalogGenericProductId'],
      });
    }
    if (body.itemType === 'GENERIC_SERVICE' && body.catalogGenericServiceId === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'catalogGenericServiceId required',
        path: ['catalogGenericServiceId'],
      });
    }
  });

export type AddQuoteItemBody = z.infer<typeof AddQuoteItemBodySchema>;

export const AddQuoteItemResponseSchema = z.object({
  quoteItemId: z.uuid(),
});

export type AddQuoteItemResponse = z.infer<typeof AddQuoteItemResponseSchema>;

export const ReusableRejectedQuoteItemSchema = z.object({
  quoteItemId: z.uuid(),
  sourceQuoteId: z.uuid(),
  sourceQuoteDisplaySequence: z.number().int(),
  itemType: z.enum(['PRODUCT', 'SERVICE', 'GENERIC_PRODUCT', 'GENERIC_SERVICE']),
  description: z.string(),
  quantity: z.string(),
  unitPriceCents: z.string(),
  totalCents: z.string(),
  currency: z.string(),
});

export type ReusableRejectedQuoteItem = z.infer<typeof ReusableRejectedQuoteItemSchema>;

export const ListReusableRejectedQuoteItemsResponseSchema = z.object({
  items: z.array(ReusableRejectedQuoteItemSchema),
});

export type ListReusableRejectedQuoteItemsResponse = z.infer<
  typeof ListReusableRejectedQuoteItemsResponseSchema
>;

export const ReuseRejectedQuoteItemBodySchema = z.object({
  sourceQuoteItemId: z.uuid(),
});

export type ReuseRejectedQuoteItemBody = z.infer<typeof ReuseRejectedQuoteItemBodySchema>;

const quoteLineCentsSchema = z.union([z.string().regex(/^\d+$/), z.number().int().nonnegative()]);

/** Positive finite quantity (integer or decimal; comma or dot). Same acceptance as add-item quantity. */
function parsePositiveLineQuantity(value: string | number): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? value : null;
  }
  const normalized = value.trim().replace(/\s/g, '').replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(normalized)) {
    return null;
  }
  const parsed = Number.parseFloat(normalized);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }
  return parsed;
}

const PositiveLineQuantitySchema = z.union([z.string(), z.number()]).superRefine((value, ctx) => {
  if (parsePositiveLineQuantity(value) === null) {
    ctx.addIssue({
      code: 'custom',
      message: 'quantity must be a positive finite number',
    });
  }
});

export const UpdateQuoteItemBodySchema = z
  .object({
    unitPriceCents: quoteLineCentsSchema,
    unitCostCents: quoteLineCentsSchema.nullable().optional(),
    quantity: PositiveLineQuantitySchema.optional(),
    /** BR-SO-217 — supplier role id, or null to clear the direct choice. */
    supplierRoleId: z.uuid().nullable().optional(),
    /** BR-SO-218 — composed part identity on the draft cotable line. */
    directPartName: z.string().nullable().optional(),
    directManufacturerName: z.string().nullable().optional(),
    directManufacturerCode: z.string().nullable().optional(),
    /** BR-SO-218 — cost blur without forcing MANUAL sale. */
    directSupplyCostOnly: z.boolean().optional(),
    /** BR-SO-219 — catalog identity swap (exactly one ref matching itemType). */
    itemType: z.enum(['PRODUCT', 'SERVICE', 'GENERIC_PRODUCT', 'GENERIC_SERVICE']).optional(),
    placeProductId: z.uuid().optional(),
    tenantServiceId: z.uuid().optional(),
    catalogGenericProductId: z.uuid().optional(),
    catalogGenericServiceId: z.uuid().optional(),
  })
  .superRefine((body, ctx) => {
    if (body.itemType === undefined) {
      return;
    }
    if (body.itemType === 'PRODUCT' && body.placeProductId === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'placeProductId required',
        path: ['placeProductId'],
      });
    }
    if (body.itemType === 'SERVICE' && body.tenantServiceId === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'tenantServiceId required',
        path: ['tenantServiceId'],
      });
    }
    if (body.itemType === 'GENERIC_PRODUCT' && body.catalogGenericProductId === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'catalogGenericProductId required',
        path: ['catalogGenericProductId'],
      });
    }
    if (body.itemType === 'GENERIC_SERVICE' && body.catalogGenericServiceId === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'catalogGenericServiceId required',
        path: ['catalogGenericServiceId'],
      });
    }
  });

export type UpdateQuoteItemBody = z.infer<typeof UpdateQuoteItemBodySchema>;

export { parsePositiveLineQuantity, PositiveLineQuantitySchema };

export const UpdateQuoteItemResponseSchema = z.object({
  quoteId: z.uuid(),
  quoteItemId: z.uuid(),
  unitPriceCents: z.string().nullable(),
  unitCostCents: z.string().nullable(),
  priceOrigin: PartLinePriceOriginSchema.nullable(),
  supplierRoleId: z.uuid().nullable().optional(),
  directPartName: z.string().nullable().optional(),
  directManufacturerName: z.string().nullable().optional(),
  directManufacturerCode: z.string().nullable().optional(),
  complementaryOfName: z.string().nullable().optional(),
});

export type UpdateQuoteItemResponse = z.infer<typeof UpdateQuoteItemResponseSchema>;

export const ApproveQuoteItemsBodySchema = z.object({
  itemIds: z.array(z.uuid()).min(1),
  customerDecisionChannel: CustomerDecisionChannelSchema.optional(),
});

export type ApproveQuoteItemsBody = z.infer<typeof ApproveQuoteItemsBodySchema>;

export const ApproveQuoteItemsResponseSchema = z.object({
  quoteId: z.uuid(),
  status: z.enum(['DRAFT', 'SENT', 'PARTIALLY_APPROVED', 'APPROVED', 'REJECTED', 'EXPIRED']),
  approvedItemIds: z.array(z.uuid()),
  partiallyApproved: z.boolean(),
  creditWarning: z
    .object({
      code: z.literal('CUSTOMER_CREDIT_EXCEEDED'),
      availableCents: z.string(),
      approvedTotalCents: z.string(),
      shortfallCents: z.string(),
    })
    .nullable()
    .optional(),
});

export type ApproveQuoteItemsResponse = z.infer<typeof ApproveQuoteItemsResponseSchema>;

export const RejectQuoteBodySchema = z.object({
  customerDecisionChannel: CustomerDecisionChannelSchema,
  rejectionReason: z.string().trim().min(1).max(500),
});

export type RejectQuoteBody = z.infer<typeof RejectQuoteBodySchema>;

export const RejectQuoteResponseSchema = z.object({
  quoteId: z.uuid(),
  status: z.literal('REJECTED'),
  rejectedAt: z.iso.datetime(),
});

export type RejectQuoteResponse = z.infer<typeof RejectQuoteResponseSchema>;

export const RejectQuoteItemsBodySchema = z.object({
  itemIds: z.array(z.uuid()).min(1),
});

export type RejectQuoteItemsBody = z.infer<typeof RejectQuoteItemsBodySchema>;

export const RejectQuoteItemsResponseSchema = z.object({
  quoteId: z.uuid(),
  rejectedItemIds: z.array(z.uuid()),
});

export const SendQuoteBodySchema = z.object({
  deliveryChannel: CustomerDeliveryChannelSchema.optional(),
  /** Portal link already shown to the operator, so WhatsApp uses the same URL. */
  approvalUrl: z.url().optional(),
});

export const IssueQuoteApprovalLinkResponseSchema = z.object({
  url: z.url(),
});

export const WorkshopQuotePdfResponseSchema = z.object({
  downloadUrl: z.url(),
  expiresAt: z.string(),
  contentType: z.literal('application/pdf'),
});

export type SendQuoteBody = z.infer<typeof SendQuoteBodySchema>;
export type IssueQuoteApprovalLinkResponse = z.infer<typeof IssueQuoteApprovalLinkResponseSchema>;
export type WorkshopQuotePdfResponse = z.infer<typeof WorkshopQuotePdfResponseSchema>;

export type RejectQuoteItemsResponse = z.infer<typeof RejectQuoteItemsResponseSchema>;

export const OpenUnboundQuoteBodySchema = z.object({
  partyId: z.uuid().optional(),
  customerRoleId: z.uuid().optional(),
  customerVehicleId: z.uuid().optional(),
  correctsServiceOrderId: z.uuid().optional(),
});

export type OpenUnboundQuoteBody = z.infer<typeof OpenUnboundQuoteBodySchema>;

export const OpenUnboundQuoteResponseSchema = z.object({
  quoteId: z.uuid(),
  displaySequence: z.number().int().positive(),
  displayNumber: z.string(),
});

export type OpenUnboundQuoteResponse = z.infer<typeof OpenUnboundQuoteResponseSchema>;

export const UpdateUnboundQuoteContextBodySchema = z.strictObject({
  partyId: z.uuid().nullable().optional(),
  customerRoleId: z.uuid().nullable().optional(),
  customerVehicleId: z.uuid().nullable().optional(),
});

export type UpdateUnboundQuoteContextBody = z.infer<typeof UpdateUnboundQuoteContextBodySchema>;

export const UpdateUnboundQuoteContextResponseSchema = z.object({
  quoteId: z.uuid(),
  partyId: z.uuid().nullable(),
  customerRoleId: z.uuid().nullable(),
  customerVehicleId: z.uuid().nullable(),
});

export type UpdateUnboundQuoteContextResponse = z.infer<
  typeof UpdateUnboundQuoteContextResponseSchema
>;

export const StartQuoteCheckInBodySchema = z.object({
  customerVehicleId: z.uuid().optional(),
  partyId: z.uuid().optional(),
  customerRoleId: z.uuid().optional(),
  receptionMode: z.enum(['FULL', 'EXPRESS']).optional(),
  entryOdometerKm: z.number().int().nonnegative().nullable().optional(),
  reportedIssue: z.string().trim().max(2000).nullable().optional(),
  notes: z.string().trim().max(2000).nullable().optional(),
});

export type StartQuoteCheckInBody = z.infer<typeof StartQuoteCheckInBodySchema>;

export const StartQuoteCheckInResponseSchema = z.object({
  receptionId: z.uuid(),
  quoteId: z.uuid(),
  displayNumber: z.string(),
});

export type StartQuoteCheckInResponse = z.infer<typeof StartQuoteCheckInResponseSchema>;
